import { useRef, useState, useContext } from "react";
import { T } from "../../hooks/useTranslate";

import { Chip, Divider, Stack, Typography } from "@mui/material";
import { useAuth } from "../../hooks/useAuth";

import { Avatar } from "@unicity/design-system";
import { CopyToClipboard } from "../../components/CopyToClipboard";

import { SettingsMenuItem } from "./SettingsMenuItem";
import { StyledSettingsMenu } from "./StyledSettingsMenu";
import { SettingsButton } from "./SettingsButton";
import "./SettingsMenu.css";

import { LanguageModal } from "./LanguageModal";
import { LanguageModalMobile } from "./LanguageModalMobile";
import { ResetApplicationModal } from "./ResetApplicationModal";
import { LanguageContext } from "../../hooks/LanguageContext";

import { RANK_DETAILS } from "../../common/rankDetails";
import { LANGUAGE_DETAILS } from "../../common/languageDetails";
import { toTitleCase } from "../../common/format-functions";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";

import { useMixPanel } from "../../hooks/useMixPanel";

import { getCountryInfo } from "../../utils/countryCodeConverter";
import { getHelpCenterUrl } from "../../common/help-center-links";
import React from "react";

import { useFlags } from 'launchdarkly-react-client-sdk';

const SettingsMenu = () => {
  const menuAnchorRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate()
  const { logEvent } = useMixPanel();

  const menuIsOpen = Boolean(anchorEl);

  const openMenu = () => setAnchorEl(menuAnchorRef.current);
  const handleClose = () => setAnchorEl(null);

  // desktop modal state
  const [open, setOpen] = useState(false);
  const handleModalOpen = () => setOpen(true);
  const handleModalClose = () => setOpen(false);

  // mobile modal state
  const [openMobile, setOpenMobile] = useState(false);
  const handleMobileModalOpen = () => setOpenMobile(true);
  const handleMobileModalClose = () => setOpenMobile(false);

  // reset-application confirm modal state (GO-3079)
  const [resetOpen, setResetOpen] = useState(false);
  const handleResetConfirm = () => {
    // Refresh-only (per ticket — logout clause struck through): a hard reload
    // drops the in-memory API cache (GO-3121) and re-runs the bootstrap
    // office-web-cache purge, resetting the app's cache without signing out.
    window.location.reload();
  };

  const { userInfo, logout } = useAuth();
  const { currentLanguage } = useContext(LanguageContext);
  const rankDetails = RANK_DETAILS.filter(
    (rank) => rank.rankCode === userInfo.highestRank
  )[0];

  const theme = useTheme();
  const viewportIsSmall = useMediaQuery(theme.breakpoints.up("sm"));
  const { unicityDomains } = useFlags();

  const getSubscriptionsDomain = () => {
    const countryInfo = getCountryInfo(userInfo?.countryCode);
    if (unicityDomains && countryInfo) {
      const alpha2Code = countryInfo.alpha2;

      if (unicityDomains.countries.includes(alpha2Code)) {
        const alpha3Code = countryInfo.alpha3.toLowerCase();
        return unicityDomains.REACT_APP_AR_DOMAIN
          .replace("{country}", alpha3Code)
          .replace("{lang}", currentLanguage.split("-")[0]);
      }
    }

    return process.env.REACT_APP_AR_PROD_DOMAIN;
  };

  return (
    <>
      <SettingsButton
        rankDetails={rankDetails}
        userInfo={userInfo}
        menuIsOpen={menuIsOpen}
        viewportIsSmall={viewportIsSmall}
        openMenu={openMenu}
        menuAnchorRef={menuAnchorRef}
      />
      <StyledSettingsMenu
        id="settingsMenu"
        anchorEl={anchorEl}
        open={menuIsOpen}
        onClose={handleClose}
        mobile={!viewportIsSmall ? "true" : "false"}
        anchorReference={viewportIsSmall ? "anchorEl" : "none"}
        sx={{
          // styles needed for parent of unanchored menu
          display: !viewportIsSmall && "flex",
          marginTop: !viewportIsSmall && "70px",
          justifyContent: !viewportIsSmall && "center",
        }}
      >
        <Stack alignItems="center" gap={2} marginBottom="32px">
          <Avatar
            id="settingsAvatar"
            alt={userInfo.name}
            src={userInfo.profilePictureUrl}
            sx={{ width: 60, height: 60 }}
            showBorder={true}
            borderColor={rankDetails?.color}
          />
          <Stack alignItems={"center"}>
            <Typography sx={{ fontSize: "calc(14px * var(--unicity-font-scale, 1))", fontWeight: "500" }} marginBottom="6px">
              {userInfo.name}
            </Typography>
            <Stack direction="row" alignItems="center" gap="4px">
              <Typography sx={{ fontSize: "calc(10px * var(--unicity-font-scale, 1))", fontWeight: "500" }}>UID: {userInfo.unicityId}</Typography>
              <CopyToClipboard
                stringToCopy={userInfo.unicityId}
                sx={{
                  color: "#153862",
                  opacity: ".6",
                  cursor: "pointer",
                }}
              />
            </Stack>
          </Stack>
          {userInfo.isAffiliate ? (
            <Chip
              data-test-id="settings-menu-affiliate-chip"
              className="u-chip"
              label={<Typography sx={{ fontSize: "calc(10px * var(--unicity-font-scale, 1))", fontWeight: "600" }}><T>affiliate</T></Typography>}
            />
          ) : (
            rankDetails?.rankTranslateFullKey && (
              <Chip
                className="u-chip"
                variant={`rank-${rankDetails?.rankId}`}
                label={<Typography sx={{ fontSize: "calc(10px * var(--unicity-font-scale, 1))", fontWeight: "600" }}><T>{rankDetails?.rankTranslateFullKey}</T></Typography>}
              />
            )
          )}
        </Stack>
        <Divider />
        <Stack className="settings-menu-item-container" gap="12px" mt={1.5}>
          <SettingsMenuItem
            id="order-history"
            icon="order-history-icon"
            text1="order_history"
            testId="orderHistoryMenuItem"
            currentLanguage={currentLanguage}
            customHref={process.env.REACT_APP_IS_PRODUCTION === "false"
              ? process.env.REACT_APP_OH_QA_DOMAIN
              : process.env.REACT_APP_OH_PROD_DOMAIN
            }
            noRedirect={true}            
            clickHandler={() => {
              logEvent("order_history", { location: "secondary_menu" });
              navigate("/orders");
            }}
          />
          <SettingsMenuItem
            id="ar-order"
            icon="shopping-bag"
            rightIcon="external"
            text1="subscriptions"
            testId="autoRefillMenuItem"
            currentLanguage={currentLanguage}
            customHref={getSubscriptionsDomain()}
            clickHandler={() =>
              logEvent("ar-shop", { location: "secondary_menu" })
            }
          />
          <SettingsMenuItem
            id="provide-feedback"
            icon="feedback"
            rightIcon="external"
            text1="provide_feedback"
            testId="provideFeedbackMenuItem"
            useHref={true}
            currentLanguage={currentLanguage}
            urlKey={"provide_feedback_url"}
            clickHandler={() =>
              logEvent("provide_feedback_view", { location: "secondary_menu" })
            }
          />
          {!userInfo.isAffiliate && (
            <SettingsMenuItem
              id="help-center"
              icon="external-help"
              rightIcon="external"
              text1="help_center"
              testId="helpCenterMenuItem"
              currentLanguage={currentLanguage}
              customHref={getHelpCenterUrl("home", currentLanguage)}
              clickHandler={() =>
                logEvent("help_center_view", { location: "secondary_menu" })
              }
            />
          )}
          <SettingsMenuItem
            id="language"
            clickHandler={
              viewportIsSmall ? handleModalOpen : handleMobileModalOpen
            }
            icon="globe"
            text1="language"
            text2={toTitleCase(
              LANGUAGE_DETAILS.filter(
                (language) => language.i18nCode === currentLanguage
              )[0].label
            )}
            testId="language-option-menu-item"
            noRedirect={true}
          />
          <SettingsMenuItem
            id="reset-application"
            clickHandler={() => setResetOpen(true)}
            icon="refresh"
            text1="reset_application"
            testId="resetApplicationMenuItem"
            noRedirect={true}
          />
          <Divider />
          <SettingsMenuItem
            id="sign-out"
            clickHandler={logout}
            icon="signout"
            text1="sign_out"
            isLogout={true}
            testId="logOutMenuItem"
            noRedirect={true}
          />
        </Stack>
      </StyledSettingsMenu>
      <LanguageModal
        open={open}
        handleModalOpen={handleModalOpen}
        handleModalClose={handleModalClose}
        handleClose={handleClose}
      />
      <LanguageModalMobile
        open={openMobile}
        handleModalOpen={handleMobileModalOpen}
        handleModalClose={handleMobileModalClose}
        handleClose={handleClose}
      />
      <ResetApplicationModal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={handleResetConfirm}
      />
    </>
  );
};

export { SettingsMenu };
